import React, { useState, useRef } from 'react';
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { theme } from '../../../../../theme';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { MessageTemplate } from '../DashboardMessages/DashboardMessage';
import { sanitizeContent } from '../../../../Common/Sanitize/sanitizeReactQuill';


interface MessageConfigurationProps {
  messageList: string;
  setMessageList: (value: string) => void;
  configureDashboardMessage: string;
  setConfigureDashboardMessage: (value: string) => void;
  handleSave: (dashboardMessage: string, configureDashboardMessage: string, messageNum: number | null) => void;
  handleDelete: (id: number | null) => void;
  setIsMessageConfigured: (value: boolean) => void;
  messageTemplates: MessageTemplate[];
  setSelectedEmailId: (value: number | null) => void;
  selectedEmailId: number | null;
  dashboardMessage: string;
  setDashboardMessage: (value: string) => void
}


const MessageConfiguration: React.FC<MessageConfigurationProps> = ({
  messageList,
  setMessageList,
  configureDashboardMessage,
  setConfigureDashboardMessage,
  handleSave,
  handleDelete,
  messageTemplates = [],
  setIsMessageConfigured,
  setSelectedEmailId,
  selectedEmailId,
  setDashboardMessage,
  dashboardMessage
}) => {
  // const [dashboardMessage, setDashboardMessage] = useState(''); // State for configured dashboard message
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  // const [selectedMessage, setSelectedMessage] = useState<number | null>(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isUserMessage, setIsUserMessage] = useState(false)
  const quillRef = useRef<ReactQuill>(null);

  const handleSaveMessage = async () => {
    const newErrors: { [key: string]: string } = {};
    if (dashboardMessage.trim().length === 0) {
      newErrors.dashboardmessage = 'Message  subject is required.'
    }
    const sanitizedValue = sanitizeContent(configureDashboardMessage)
    if (!sanitizedValue) {
      newErrors.configuredashboardmessage = 'Message body is required.'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return
    }

    await handleSave(dashboardMessage, configureDashboardMessage, selectedEmailId)
    setMessageList('')
    setDashboardMessage('')
    setConfigureDashboardMessage('')
    setSelectedEmailId(null)
    setIsMessageConfigured(false)
    setIsUserMessage(false)
  }

  const handleMessageListChange = (event: any) => {
    const newValue = event.target.value;
    setMessageList(newValue);

    // Find the selected template from the messageTemplates array
    const selectedTemplate = messageTemplates.find(
      (template) => template.MessageSubject === newValue
    );

    // If template found, set the respective states
    if (selectedTemplate) {
      setSelectedEmailId(selectedTemplate.MessageNum)
      setDashboardMessage(selectedTemplate.MessageSubject);
      setConfigureDashboardMessage(selectedTemplate.MessageBody);
      setIsMessageConfigured(true)
      setIsUserMessage(selectedTemplate.IsUserMessage)
    } else {
      setDashboardMessage('');
      setConfigureDashboardMessage('');
      setIsMessageConfigured(false)
      setIsUserMessage(false)
    }
  };

  const handleClear = () => {
    // Clear the message list, dashboard message, and editor content
    setMessageList('');
    setDashboardMessage('');
    setConfigureDashboardMessage('');
    setSelectedEmailId(null)
    setIsMessageConfigured(false)
    setIsUserMessage(false)
  };



  const handleDeleteMessage = async () => {
    await handleDelete(selectedEmailId)
    handleClear()
    setOpenDeleteDialog(false)
  }

  const handleCancelLogout = () => {
    setOpenDeleteDialog(false);
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'], // Bold, Italic, Underline
      [{ list: 'ordered' }, { list: 'bullet' }], // Bullet points and numbered points
      ['link'], // Link
      [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }], // Align options
      [{ 'indent': '-1' }, { 'indent': '+1' }],
    ],
    clipboard: {
      matchVisual: false, // Prevents Quill from auto-converting visual line breaks to `<p><br></p>`
    },
  };

  const handleInputChange = (value: string, field: string) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));
    switch (field) {
      case "dashboardmessage":
        setDashboardMessage(value);
        break;
      case "configuredashboardmessage":
        setConfigureDashboardMessage(value);
        break;
      default:
        break;
    }
  };

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      gap={2.2}
      sx={{

        p: '1vw',
        pb: 0, // Padding inside the box
        backgroundColor: 'background.paper', // Use theme's paper background color
        borderRadius: '1vw', // Rounded corners
        border: `0.0694vw solid ${theme.palette.custom.borderLight}`, // Adds a shadow for a 'contained' effect
      }}
    >

      <Box sx={{ display: 'flex', flexDirection: 'column' }} gap={2}>
        {/* Dropdown for selecting the message template using Select */}
        <FormControl size="small" sx={{ mb: '1vw', width: '100%' }}>
          <InputLabel id="message-list-label">Configured Message List</InputLabel>
          <Select
            labelId="message-list-label"
            id="message-list"
            value={messageList}
            onChange={handleMessageListChange}
            label="Configured Message List"
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: '20vw', // You can adjust this as needed
                },
              },
            }}
          >
            {Array.isArray(messageTemplates) && messageTemplates.length > 0 ? (
              messageTemplates.map((template) => (
                <MenuItem key={template.MessageNum} value={template.MessageSubject}>
                  {template.MessageSubject}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No templates available</MenuItem>
            )}
          </Select>
        </FormControl>


        {/* TextField for configured dashboard message */}
        <TextField
          fullWidth
          label="Configure Dashboard Message"
          value={dashboardMessage} // Bind the dashboard message state here
          onChange={(e) => handleInputChange(e.target.value, "dashboardmessage")} // Update dashboard message state
          variant="outlined"
          size="small"
          sx={{ mb: '1vw' }}
          error={!!errors.dashboardmessage}
          helperText={errors.dashboardmessage}
          autoComplete='off'
        />
      </Box>

      <Typography
        variant="caption"
      >
        Body Content <span style={{ color: theme.palette.error.main }}>* </span>
      </Typography>

      {/* Message Editor using React Quill */}
      <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: '2vw' }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          className="my-quill-editor"
          value={configureDashboardMessage}
          onChange={(value) => handleInputChange(value, "configuredashboardmessage")}
          modules={quillModules}
          style={{ height: '20vh', marginBottom: '2vh' }} // Adjust height based on viewport height
        />
        {errors.configuredashboardmessage && (
          <Typography color="error" variant="body2" sx={{ mt: 4, ml: 2 }}>
            {errors.configuredashboardmessage}
          </Typography>
        )}
      </Box>

      {/* Action buttons */}
      {/* Action buttons */}
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        sx={{
          borderBottomLeftRadius: '1.11vw',
          borderBottomRightRadius: '1.11vw',
          backgroundColor: theme.palette.primary.light,
          p: '1vw 3vw', // Padding adjusted to match the container
          width: 'calc(100% + 2vw)',
          position: 'relative',
          right: '1vw', // Reset any offset
        }}
      >
        <Button variant="outlined" color="secondary" onClick={handleClear} sx={{ minWidth: '100px' }}>
          Clear
        </Button>
        {(selectedEmailId && isUserMessage) && (
          <Button
            variant="outlined"
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              minWidth: '100px',
            }}
            onClick={() => { setOpenDeleteDialog(true) }}
          >
            Delete
          </Button>
        )}

        <Button variant="contained" color="primary" onClick={handleSaveMessage} sx={{ minWidth: '100px' }}>
          Save
        </Button>
      </Box>

      <Dialog open={openDeleteDialog} onClose={handleCancelLogout}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this message? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout} sx={{ color: theme.palette.text.secondary }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteMessage}

            variant="contained"
            sx={{
              bgcolor: theme.palette.error.main,
              '&:hover': {
                bgcolor: theme.palette.error.dark,
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageConfiguration;
